import * as ex from 'excalibur';
import { ActionType, Allegiance, ControlType } from '../types/enums';
import { Action } from '../types/interfaces';
import { TILE_SIZE } from '../config/constants';
import { Unit } from '../core/unit';
import { Grid } from '../core/grid';
import { PlayerUnit } from './playerUnit';

// Merk unit - attacks the weakest enemy in a straight line
export class Merk extends Unit {
  constructor(options: {
    gridX: number,
    gridY: number,
    allegiance: Allegiance,
    initiative: number,
    controlType?: ControlType,
    grid: Grid
  }) {
    super({
      pos: new ex.Vector(options.gridX * TILE_SIZE + TILE_SIZE / 2, options.gridY * TILE_SIZE + TILE_SIZE / 2),
      gridX: options.gridX,
      gridY: options.gridY,
      type: 'merk',
      name: 'Merk',
      width: TILE_SIZE * 0.75,
      height: TILE_SIZE * 0.75,
      color: options.allegiance === Allegiance.Player ? ex.Color.Blue : ex.Color.Red,
      initiative: options.initiative,
      allegiance: options.allegiance,
      health: 30,
      maxHealth: 30,
      controlType: options.controlType || (options.allegiance === Allegiance.Player ?
        ControlType.Manual : ControlType.Automatic),
      moveRange: 2,
      attackRange: 1,
      abilities: ['straight_line_attack'],
      grid: options.grid
    });

    // Set the emoji for this unit type
    this.setEmoji("⚔️"); // Sword for warrior/fighter type
  }

  public decideAction(): Action | null {
    // If manual control and player unit, delegate to player input
    if (this.controlType === ControlType.Manual && this instanceof PlayerUnit) {
      return super.decideAction();
    }

    // Check for enemies in attack range
    const attackTargets = this.findTargetsInRange(this.attackRange);
    if (attackTargets.length > 0) {
      // Sort by health to find weakest
      attackTargets.sort((a, b) => a.health - b.health);
      return this.createAttackAction(attackTargets[0]);
    }

    // Look for enemies within 2 rings to move towards
    const targets = this.findTargetsInRange(this.moveRange * 2);
    if (targets.length > 0) {
      // Sort by health to find weakest
      targets.sort((a, b) => a.health - b.health);
      const target = targets[0];

      // Find direction to target (straight line only)
      const dx = target.gridX - this.gridX;
      const dy = target.gridY - this.gridY;

      // Prioritize larger direction
      if (Math.abs(dx) >= Math.abs(dy)) {
        // Move horizontally
        const moveX = this.gridX + Math.sign(dx) * Math.min(Math.abs(dx), this.moveRange);
        if (this.grid.isValidMove(moveX, this.gridY)) {
          return this.createMoveAction(moveX, this.gridY);
        }
      } else {
        // Move vertically
        const moveY = this.gridY + Math.sign(dy) * Math.min(Math.abs(dy), this.moveRange);
        if (this.grid.isValidMove(this.gridX, moveY)) {
          return this.createMoveAction(this.gridX, moveY);
        }
      }
    }

    // If no targets or can't move toward target, move randomly
    const possibleMoves = [
      { x: this.gridX + this.moveRange, y: this.gridY },
      { x: this.gridX - this.moveRange, y: this.gridY },
      { x: this.gridX, y: this.gridY + this.moveRange },
      { x: this.gridX, y: this.gridY - this.moveRange }
    ].filter(pos => this.grid.isValidMove(pos.x, pos.y));

    if (possibleMoves.length > 0) {
      const randomMove = possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
      return this.createMoveAction(randomMove.x, randomMove.y);
    }

    // If no valid moves, wait
    return this.createWaitAction();
  }
}